/** Проверка допработ за период (10.10.2026, владелец): «есть дублирующиеся функции… сделай
 *  отдельное рабочее поле для проверки доп-работ за период». Чистая логика окна ExtraReview:
 *  все допработы периода (назначенные и заявки — каждая строка один раз) склеиваются с
 *  зарплатным расчётом (игры, админства, сколько реально заплатим) и ищется двойная оплата.
 *
 *  Деньги — как в зарплате (routes/finance.ts): допработа по часам теряет время игр (игра =
 *  90 мин от начала); сумма, вписанная ОО вручную (rate_override), платится целиком — игры её
 *  НЕ уменьшают; админство платится целиком, игры в смену не вычитаются. Окно ничего не
 *  пересчитывает за зарплату — показывает её числа и предупреждает, где выходит двойная оплата. */
import type { ExtraWork } from "../../hooks/useExtraWork";

export const GAME_MIN = 90; /* как в зарплате: «game time = 90 min hardcoded» */
const NIGHT = 360; /* до 06:00 — ночной хвост бизнес-дня (NIGHT_CUTOFF) */

export interface SalaryDay {
  quests?: { booking_id?: number; time?: string; short_name?: string; quest_name?: string; status?: string }[];
  hall_admin?: { branch?: string; branch_id?: string; hours?: number; rate?: number; earning?: number; time_from?: string; time_to?: string; reported?: boolean } | null;
  extra_work?: { id: number; earning?: number; raw_minutes?: number; effective_minutes?: number; time_from?: string; hourly_rate?: number;
    /** 15.10: разбивка зарплаты — с 15.10 игры вычитаются настоящей длительностью (utils/extraWorkPay.ts) */ pay_rule?: "flat90" | "real"; deductions?: { time: string; quest: string; minutes: number }[] }[];
}
export interface SalaryResp { results?: { staffId: string; name?: string; days?: Record<string, SalaryDay> }[] }

export interface RvGame { time: string; quest: string; cancelled: boolean }
export interface RvAdmin { branch: string; branchId: string; from: string; to: string; hours: number; rate: number; earning: number; reported: boolean; warn: string | null }
export type RvState = "paid" | "proposal" | "rework" | "rejected" | "study";
/** factWait — по отчёту/запросу оператора другое время, ОО не подтвердил (платим план);
 *  outOfSalary — назначена, но зарплата её не платит (не оператор, уволен, время съели игры). */
/** 11.10 (владелец: «часы и деньги — формулой, если был автовычет игры»; «она 6 или 7 часов работала? вычет 4,5 или 5,5?»):
 *  расчёт ровно как в зарплате — какое время берём (план или подтверждённый отчёт), минус каждая игра той же даты
 *  (90 мин от начала; отменённые НЕ вычитаются — 11.10, владелец), оплачиваемые часы × ставка. */
export interface RvCalc { basis: "план" | "по отчёту"; from: string; to: string; raw: number; games: { label: string; min: number }[]; eff: number; rate: number; byHoursPay: number }
export interface RvWork { w: ExtraWork; state: RvState; pay: number; byHours: boolean; note: string | null; warns: string[]; factWait: boolean; outOfSalary: boolean; payable: boolean; calc: RvCalc | null }
/** games — игры бизнес-дня (свои с 06:00 + ночные следующей даты), salaryGames — список, которым
 *  зарплата вычитает игры из допработы (все игры той же календарной даты). */
export interface RvPerson { operatorId: string; name: string; games: RvGame[]; salaryGames: RvGame[]; admin: RvAdmin | null; works: RvWork[]; warn: boolean }
export interface RvDay { date: string; people: RvPerson[] }

const hm = (t: string | null | undefined): number | null => {
  const m = /^(\d{1,2}):(\d{2})/.exec(t ?? "");
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};
/** Интервал бизнес-дня в минутах: до 06:00 — после полуночи, конец ≤ начала — через полночь. */
export function span(from: string | null | undefined, to: string | null | undefined): [number, number] | null {
  let a = hm(from), b = hm(to);
  if (a == null || b == null) return null;
  if (a < NIGHT) a += 1440;
  if (b < NIGHT) b += 1440;
  if (b < a) b += 1440;
  return b > a ? [a, b] : null;
}
const cut = (x: [number, number], y: [number, number]): number => Math.max(0, Math.min(x[1], y[1]) - Math.max(x[0], y[0]));
/** Игра: начало + 90 мин (ночная — после полуночи бизнес-дня). */
const gameIv = (g: RvGame): [number, number] | null => { let a = hm(g.time); if (a == null) return null; if (a < NIGHT) a += 1440; return [a, a + GAME_MIN]; };
/** Время, за которое платят: факт, если ОО его подтвердил, иначе план (как в зарплате). */
const workIv = (w: ExtraWork): [number, number] | null => (w.factOk && w.factFrom && w.factTo ? span(w.factFrom, w.factTo) : span(w.timeFrom, w.timeTo));
const minutes = (n: number): string => (n % 60 === 0 ? `${n / 60} ч` : n > 60 ? `${Math.floor(n / 60)} ч ${n % 60} мин` : `${n} мин`);
/** Часы, как говорят в офисе: 360 → «6 ч», 90 → «1,5 ч», 270 → «4,5 ч»; то, что десятыми точно не записать
 *  (340 → «5 ч 40 мин», 20 → «20 мин»), — часами и минутами, чтобы произведение в формуле сходилось с суммой. */
export const hrs = (min: number): string => (min % 6 === 0 ? `${String(min / 60).replace(".", ",")} ч`
  : min < 60 ? `${min} мин` : `${Math.floor(min / 60)} ч ${min % 60} мин`);
/** Игры той же даты, которые зарплата вычитает из интервала, — её формулой (без ночного сдвига, 90 мин). */
function gameCuts(from: string, to: string, games: RvGame[]): { label: string; min: number }[] {
  const a = hm(from) ?? 0, b0 = hm(to) ?? 0, b = b0 < a ? b0 + 1440 : b0;
  return games.filter((g) => !g.cancelled).map((g) => { const s = hm(g.time) ?? 0; return { label: `${g.time}${g.quest ? ` ${g.quest}` : ""}`, min: Math.max(0, Math.min(b, s + GAME_MIN) - Math.max(a, s)) }; })
    .filter((x) => x.min > 0);
}

/** Сколько вычтет зарплата за игры — её же формулой (finance.ts): без ночного сдвига, игра = 90 мин. */
function salaryDeduct(from: string, to: string, games: RvGame[]): { raw: number; eff: number } {
  const a = hm(from) ?? 0, b0 = hm(to) ?? 0, b = b0 < a ? b0 + 1440 : b0;
  let eff = Math.max(0, b - a);
  for (const g of games) { if (g.cancelled) continue; const s = hm(g.time) ?? 0; eff -= Math.max(0, Math.min(b, s + GAME_MIN) - Math.max(a, s)); }   /* отмену не вычитаем (11.10) */
  return { raw: Math.max(0, b - a), eff: Math.max(0, eff) };
}

const nextDay = (d: string): string => { const [y, m, dd] = d.split("-").map(Number); return new Date(Date.UTC(y ?? 2026, (m ?? 1) - 1, (dd ?? 1) + 1)).toISOString().slice(0, 10); };
type Quest = NonNullable<SalaryDay["quests"]>[number];
const toGame = (q: Quest): RvGame => ({ time: String(q.time).slice(0, 5), quest: String(q.short_name || q.quest_name || ""), cancelled: q.status === "cancelled" });

const stateOf = (w: ExtraWork): RvState =>
  w.kind === "training" ? "study"
    : w.status === "proposed" ? "proposal" : w.status === "rework" ? "rework" : w.status === "rejected" ? "rejected" : "paid";

/** Окно: дни периода → люди с допработами или админством; у каждого — игры дня для сверки.
 *  Зарплату запрашивать по `to` + 1 день: ночные игры последнего дня записаны следующей датой. */
export function buildReview(works: ExtraWork[], sal: SalaryResp, from: string, to: string): RvDay[] {
  const staff = new Map((sal.results ?? []).map((s) => [String(s.staffId), s]));
  const people = new Map<string, RvPerson>(); /* ключ «дата|оператор» */
  const person = (date: string, id: string, name: string): RvPerson => {
    const k = `${date}|${id}`;
    let p = people.get(k);
    if (!p) {
      const days = staff.get(id)?.days;
      const own = (days?.[date]?.quests ?? []).filter((q) => q.time), night = (days?.[nextDay(date)]?.quests ?? []).filter((q) => q.time);
      const games = [...own.filter((q) => (hm(q.time) ?? 0) >= NIGHT), ...night.filter((q) => (hm(q.time) ?? NIGHT) < NIGHT)].map(toGame);
      p = { operatorId: id, name, games, salaryGames: own.map(toGame), admin: null, works: [], warn: false };
      people.set(k, p);
    }
    return p;
  };
  for (const s of sal.results ?? []) {
    for (const [date, day] of Object.entries(s.days ?? {})) {
      const ha = day.hall_admin;
      if (!ha || date < from || date > to) continue;
      const p = person(date, String(s.staffId), s.name ?? String(s.staffId));
      const iv = span(ha.time_from, ha.time_to);
      const hit = iv ? p.games.filter((g) => { const gi = gameIv(g); return !g.cancelled && gi && cut(iv, gi) > 0; }) : [];   /* отмену не играли — не двойная оплата */
      const hitMin = iv ? hit.reduce((n, g) => n + cut(iv, gameIv(g)!), 0) : 0;
      p.admin = {
        branch: String(ha.branch ?? ha.branch_id ?? ""), branchId: String(ha.branch_id ?? ""), from: String(ha.time_from ?? ""), to: String(ha.time_to ?? ""),
        hours: Number(ha.hours) || 0, rate: Number(ha.rate) || 0, earning: Number(ha.earning) || 0, reported: !!ha.reported,
        warn: hit.length ? `во время смены игр${hit.length > 1 ? "ы" : "а"} ${hit.map((g) => g.time).join(", ")} (${minutes(hitMin)}) — смена оплачена целиком, двойная оплата?` : null,
      };
    }
  }
  for (const w of works) {
    if (w.date < from || w.date > to) continue;
    const p = person(w.date, w.operatorId, w.operatorName);
    const state = stateOf(w);
    const useFact = w.factOk && !!w.factFrom && !!w.factTo;
    const f = useFact ? w.factFrom! : w.timeFrom, t = useFact ? w.factTo! : w.timeTo;
    const byHours = w.rate_override == null;
    const hit = staff.get(w.operatorId)?.days?.[w.date]?.extra_work?.find((e) => e.id === w.id);
    /* строка «Такси за …» с тем же id (время пустое, ₽0) — не оплата работы (как dayWorkPay, 03.10) */
    const paid = hit && !(hit.time_from === "" && !hit.raw_minutes && !hit.earning) ? hit : undefined;
    const payable = w.operatorPaid !== false;   /* ДК/ОО зарплата не считает вовсе */
    const ded = paid ? { raw: Number(paid.raw_minutes) || 0, eff: Number(paid.effective_minutes) || 0 } : salaryDeduct(f, t, p.salaryGames);
    /* 10.10 (владелец: «в расчётке нет»): назначенная, которой нет в зарплате, — ₽0 с причиной, а не своя оценка */
    const outOfSalary = state === "paid" && !paid;
    const factWait = !w.factOk && !!w.factFrom && !!w.factTo && (w.factFrom !== w.timeFrom || w.factTo !== w.timeTo);
    const pay = state === "study" || outOfSalary ? 0 : paid ? Number(paid.earning) || 0
      : byHours ? Math.round(((w.hourlyRate || 0) * ded.eff) / 60) : Number(w.rate_override) || 0;
    const iv = workIv(w);
    /* 15.10: с 15.10 зарплата вычитает игру её настоящей длительностью — сверяем с её вычетами, а не с «90 мин»
       (иначе короткая игра давала ложное «не вычтено, двойная оплата?»); тревога — только за игры, которых зарплата не вычла */
    const real = paid?.pay_rule === "real" ? (paid.deductions ?? []) : null;
    const gameHit = iv ? p.games.filter((g) => { const gi = gameIv(g); return !g.cancelled && gi && cut(iv, gi) > 0 && !(real && byHours && real.some((d) => d.time === g.time)); }) : [];
    const gameMin = iv ? gameHit.reduce((n, g) => n + cut(iv, gameIv(g)!), 0) : 0;
    const warns: string[] = [];
    let note: string | null = null;
    if (state !== "study" && state !== "rejected" && !outOfSalary && gameMin > 0) {
      const at = gameHit.map((g) => `${g.time}${g.quest ? ` ${g.quest}` : ""}`).join(", ");
      if (!byHours) warns.push(`сумма вписана вручную, а в это время игра ${at} (${hrs(gameMin)}) — игра её не уменьшает, двойная оплата?`);
      else if (real) warns.push(`игра ${at} — не вычтена из оплаты (${hrs(gameMin)}), двойная оплата?`);
      else if (gameMin > ded.raw - ded.eff) warns.push(`игра ${at} — ${hrs(gameMin - (ded.raw - ded.eff))} не вычтено из оплаты, двойная оплата?`);
      /* вычет игры виден формулой (calc), отдельной заметки не нужно */
    }
    if (outOfSalary) note = !payable ? "в расчётку не входит: зарплата считает только операторов (ДК, ОО — нет)"
      : "в расчётку не попала: время нулевое или целиком перекрыто играми (или человек уволен) — проверьте часы";
    const rate = Number(paid?.hourly_rate ?? w.hourlyRate) || 0;
    const calc: RvCalc | null = state === "study" || !span(f, t) ? null
      : { basis: useFact ? "по отчёту" : "план", from: f, to: t, raw: ded.raw, eff: ded.eff, rate, byHoursPay: Math.round((rate * ded.eff) / 60),
        games: real ? real.map((d) => ({ label: `${d.time}${d.quest ? ` ${d.quest}` : ""}`, min: d.minutes })) : gameCuts(f, t, p.salaryGames) };
    p.works.push({ w, state, pay, byHours, note, warns, factWait, outOfSalary, payable, calc });
  }
  /* пересечения внутри человека: допработа × своё админство, допработа × другая допработа */
  for (const p of people.values()) {
    const admIv = p.admin ? span(p.admin.from, p.admin.to) : null;
    const live = p.works.filter((x) => x.state !== "rejected" && x.state !== "study" && !x.outOfSalary);
    for (const x of live) {
      const iv = workIv(x.w);
      if (!iv) continue;
      if (admIv && cut(iv, admIv) > 0) x.warns.push(`одновременно со своим админством (${minutes(cut(iv, admIv))}) — двойная оплата?`);
      for (const y of live) {
        if (y === x) continue;
        const jv = workIv(y.w);
        if (jv && cut(iv, jv) > 0) x.warns.push(`пересекается с допработой ${y.w.timeFrom}–${y.w.timeTo} (${y.state === "paid" ? "назначена" : "заявка"}) — дубль?`);
      }
    }
    p.works.sort((a, b) => (span(a.w.timeFrom, a.w.timeTo)?.[0] ?? 0) - (span(b.w.timeFrom, b.w.timeTo)?.[0] ?? 0));
    p.warn = !!p.admin?.warn || p.works.some((x) => x.warns.length > 0);
  }
  const days = new Map<string, RvPerson[]>();
  for (const [k, p] of people) { const d = k.slice(0, 10); days.set(d, [...(days.get(d) ?? []), p]); }
  return [...days.entries()].sort(([a], [b]) => a.localeCompare(b))
    .map(([date, ps]) => ({ date, people: ps.sort((a, b) => a.name.localeCompare(b.name, "ru")) }));
}

export type RvFilter = "all" | "proposals" | "warn";
/** Фильтр окна: «заявки» — люди с заявкой или возвратом, «конфликты» — с предупреждением. */
export function filterReview(days: RvDay[], f: RvFilter, operatorId: string | null): RvDay[] {
  return days.map((d) => ({
    ...d,
    people: d.people.filter((p) => (!operatorId || p.operatorId === operatorId)
      && (f === "all" || (f === "warn" ? p.warn : p.works.some((x) => x.state === "proposal" || x.state === "rework" || (x.factWait && x.state !== "rejected"))))),
  })).filter((d) => d.people.length > 0);
}

/** rework/factWait — тоже «ждут решения ОО»: без него работа в расчётке по плану или не входит вовсе. */
export interface RvTotals { paid: number; admin: number; proposals: number; proposalSum: number; warns: number; rework: number; factWait: number }
export function reviewTotals(days: RvDay[]): RvTotals {
  const t: RvTotals = { paid: 0, admin: 0, proposals: 0, proposalSum: 0, warns: 0, rework: 0, factWait: 0 };
  for (const d of days) for (const p of d.people) {
    t.admin += p.admin?.earning ?? 0;
    if (p.admin?.warn) t.warns += 1;
    for (const x of p.works) {
      if (x.state === "paid") t.paid += x.pay;
      if (x.state === "proposal") { t.proposals += 1; t.proposalSum += x.pay; }
      if (x.state === "rework") t.rework += 1;
      if (x.factWait && x.state !== "rejected") t.factWait += 1;
      t.warns += x.warns.length;
    }
  }
  return t;
}

